import { FC, ReactNode } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons'
import { noop } from '@utils/noop'
import { twJoin, twMerge } from 'tailwind-merge'

interface PickerRenderOptions {
  hidden: boolean
}

interface Props {
  autoFocus?: boolean
  className?: string
  searchFieldClassName?: string
  disabled?: boolean
  error?: string
  hidePicker?: boolean
  onBlur?: () => void
  onChange: (newValue: string) => void
  onEnter?: () => void
  onFocus?: () => void
  picker?: (options: PickerRenderOptions) => ReactNode
  pickerVariant?: 'floating' | 'fixed-below'
  placeholder: string
  value: string
}

export const SearchField: FC<Props> = ({
  autoFocus = false,
  className,
  searchFieldClassName,
  disabled,
  error,
  hidePicker = true,
  onBlur,
  onChange,
  onEnter = noop,
  onFocus,
  picker,
  pickerVariant = 'floating',
  placeholder,
  value,
}) => {
  const fixedBelowPickerVisible =
    picker && !hidePicker && pickerVariant === 'fixed-below'

  return (
    <div
      className={twMerge(
        'flex flex-col',
        fixedBelowPickerVisible && 'overflow-hidden',
        className,
      )}
    >
      <div
        className={twMerge(
          'relative px-4 grow flex items-center text-sm border-solid border-[1px] bg-neutral-100 text-neutral-500 flex-nowrap',
          searchFieldClassName,
          picker && !hidePicker && pickerVariant === 'floating'
            ? 'rounded-b-none rounded-t-3xl border-2 border-primary-600 border-b border-b-neutral-400'
            : 'rounded-primary border-neutral-400',
        )}
      >
        <FontAwesomeIcon className="mr-2" icon={faSearch} />
        <input
          className="w-0 grow overflow-hidden py-2.5 outline-none bg-transparent"
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onBlur={onBlur}
          onChange={(event) => onChange(event.currentTarget.value)}
          onKeyUp={(event) => {
            event.key === 'Enter' && onEnter()
          }}
          onFocus={onFocus}
          autoFocus={autoFocus}
        />
        <FontAwesomeIcon
          className={twJoin(
            value ? 'opacity-100 cursor-pointer' : 'opacity-0',
            'ml-2',
          )}
          icon={faXmarkCircle}
          onClick={() => onChange('')}
        />
        {picker && !hidePicker && pickerVariant === 'floating' && (
          <div className="absolute left-[-2px] right-[-2px] flex flex-col top-[100%] border-2 border-primary-600 border-t-0 rounded-b-3xl overflow-hidden p-4 bg-white z-40">
            <div className="max-h-[250px] flex flex-col overflow-auto">
              {picker({ hidden: hidePicker })}
            </div>
          </div>
        )}
      </div>
      {error && (
        <span className="mt-1 text-[11.5px] text-neutral-500">{error}</span>
      )}
      <div className="overflow-auto">
        {fixedBelowPickerVisible && picker({ hidden: hidePicker })}
      </div>
    </div>
  )
}
